/**
 * The career calendar.
 *
 * A career date is a **month index and a day**, mirroring the pilot save file's
 * own encoding rather than inventing one: `MBPLTREC.DOC`'s documented
 * `ROSTER.DAT` record stores "current month (`0x01` = Dec 1915 … `0x23` =
 * Oct 1918), day 1–28" (docs/formats/missions-campaign.md § *`ROSTER.DAT`*).
 * So month 1 is December 1915 — the game's earliest enlistment — and every
 * month is 28 days long, which is the original's own simplification, not ours.
 *
 * We are not binary-compatible (this port keeps its own JSON save), but the
 * field shape is worth mirroring: it makes "four months with your squadron
 * before another transfer" and "three months with your personal aircraft"
 * (docs/game/campaign.md) exact integer comparisons rather than approximations.
 *
 * The war ends **10 November 1918** — month 36, day 10 — which is the manual's
 * own date for the end of a career ("through to the end of the war on November
 * 10, 1918", manual, "Career Play"), one day before the real Armistice.
 * `ROSTER.DAT`'s month field stops at October 1918 because a career ends in the
 * month after it; the index simply continues.
 */

import type { MissionEra } from "../mission/mission-world.ts";

/** Every month in this calendar is 28 days, per `ROSTER.DAT`'s day field. */
export const DAYS_PER_MONTH = 28;

/** Month index 1 is December 1915. */
export const FIRST_MONTH_YEAR = 1915;
const FIRST_MONTH_OF_YEAR = 11;

export interface CareerDate {
  /** 1 = December 1915, 2 = January 1916, … 36 = November 1918. */
  readonly month: number;
  /** 1–28. */
  readonly day: number;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/** The month index for a calendar year and a 0-based month, e.g. `1918, 4` → May 1918. */
export function monthIndexOf(year: number, monthOfYear: number): number {
  return (year - FIRST_MONTH_YEAR) * 12 + (monthOfYear - FIRST_MONTH_OF_YEAR) + 1;
}

export function careerDate(month: number, day: number): CareerDate {
  return { month, day };
}

export function yearOf(date: CareerDate): number {
  return FIRST_MONTH_YEAR + Math.floor((FIRST_MONTH_OF_YEAR + date.month - 1) / 12);
}

/** 0-based month of the calendar year. */
export function monthOfYearOf(date: CareerDate): number {
  return (FIRST_MONTH_OF_YEAR + date.month - 1) % 12;
}

export function monthName(date: CareerDate): string {
  return MONTH_NAMES[monthOfYearOf(date)] ?? "January";
}

/** `"14 April 1918"` — the pilot record's own date line. */
export function formatCareerDate(date: CareerDate): string {
  return `${date.day} ${monthName(date)} ${yearOf(date)}`;
}

/** `"December of 1915"` — the enlistment sentence's own phrasing (`PS.EXE`). */
export function formatCareerMonth(date: CareerDate): string {
  return `${monthName(date)} of ${yearOf(date)}`;
}

/** Days since the start of the calendar, so two dates can be compared or subtracted. */
export function absoluteDay(date: CareerDate): number {
  return (date.month - 1) * DAYS_PER_MONTH + (date.day - 1);
}

export function fromAbsoluteDay(day: number): CareerDate {
  const clamped = Math.max(0, Math.floor(day));
  return { month: Math.floor(clamped / DAYS_PER_MONTH) + 1, day: (clamped % DAYS_PER_MONTH) + 1 };
}

export function addDays(date: CareerDate, days: number): CareerDate {
  return fromAbsoluteDay(absoluteDay(date) + days);
}

export function daysBetween(from: CareerDate, to: CareerDate): number {
  return absoluteDay(to) - absoluteDay(from);
}

/** Whole months elapsed — what the transfer and personal-aircraft cooldowns count. */
export function monthsBetween(from: CareerDate, to: CareerDate): number {
  return Math.floor(daysBetween(from, to) / DAYS_PER_MONTH);
}

export function compareDates(left: CareerDate, right: CareerDate): number {
  return absoluteDay(left) - absoluteDay(right);
}

export function isOnOrAfter(date: CareerDate, other: CareerDate): boolean {
  return compareDates(date, other) >= 0;
}

/** 10 November 1918 — the manual's own end of the war. */
export const WAR_END_DATE: CareerDate = { month: monthIndexOf(1918, 10), day: 10 };

/** 1 April 1918: the RFC and the RNAS become the Royal Air Force. */
export const RAF_FORMED_DATE: CareerDate = { month: monthIndexOf(1918, 3), day: 1 };

export const ENLISTMENT_PERIODS = ["early", "middle", "late"] as const;

export type EnlistmentPeriod = (typeof ENLISTMENT_PERIODS)[number];

/** The three enlistment dates, verbatim from `PS.EXE` (docs/game/campaign.md). */
export const ENLISTMENT_DATES: Readonly<Record<EnlistmentPeriod, CareerDate>> = {
  early: { month: monthIndexOf(1915, 11), day: 1 },
  middle: { month: monthIndexOf(1916, 9), day: 1 },
  late: { month: monthIndexOf(1918, 0), day: 1 },
};

/** The label the enlistment picker shows, verbatim. */
export const ENLISTMENT_LABELS: Readonly<Record<EnlistmentPeriod, string>> = {
  early: "early in the war",
  middle: "in the middle of the war",
  late: "late in the war",
};

/**
 * Which of the mission layer's three eras a date falls in. The boundaries are
 * the enlistment dates themselves, which is what the era buckets were derived
 * from in the first place (`mission-roster.ts`).
 */
export function eraOf(date: CareerDate): MissionEra {
  if (compareDates(date, ENLISTMENT_DATES.middle) < 0) return "early";
  if (compareDates(date, ENLISTMENT_DATES.late) < 0) return "mid";
  return "late";
}
